import { useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { localizedComplaints } from "@/data/complaints.en";
import { useLang, useT, useLocalePath } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { ArrowRight } from "lucide-react";

const Beschwerden = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const complaints = useMemo(() => localizedComplaints(lang), [lang]);
  const t = useT(
    {
      collection: "Beschwerdebilder – PRAXIS IB Wien",
      home: "Startseite",
      complaints: "Beschwerden",
      breadcrumbLabel: "Brotkrumen",
      h1: "Beschwerdebilder, bei denen ich Sie begleite",
      intro:
        "Zu den häufigsten Anliegen finden Sie hier ausführliche Seiten: was dahinterstecken kann, welche Diagnostik sinnvoll ist und wie ich in meiner Praxis in Wien vorgehe.",
      more: "Mehr dazu lesen",
      title: "Beschwerden A–Z: Reizdarm, SIBO, Blase & Haut | PRAXIS IB",
      description:
        "Themenseiten zu Reizdarm, SIBO, chronischer Blasenentzündung, Haut, Histaminintoleranz und Infektanfälligkeit – komplementärmedizinisch begleitet in Wien.",
      inLanguage: "de-AT",
    },
    {
      collection: "Health concerns – PRAXIS IB Vienna",
      home: "Home",
      complaints: "Health concerns",
      breadcrumbLabel: "Breadcrumb",
      h1: "Health concerns I support you with",
      intro:
        "For the most common concerns you will find detailed pages here: what may lie behind them, which diagnostics make sense and how I work in my practice in Vienna.",
      more: "Read more",
      title: "Health concerns A–Z: IBS, SIBO, bladder, skin | PRAXIS IB",
      description:
        "Topic pages on IBS, SIBO, recurrent cystitis, skin conditions, histamine intolerance, recurrent infections and stress – complementary medicine support in Vienna.",
      inLanguage: "en",
    },
  );

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: t.collection,
        url: absoluteUrl("/beschwerden"),
        inLanguage: t.inLanguage,
        hasPart: complaints.map((c) => ({
          "@type": "MedicalWebPage",
          name: c.title,
          url: absoluteUrl(`/beschwerden/${c.slug}`),
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: t.complaints, item: absoluteUrl("/beschwerden") },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [complaints, lang],
  );

  useSeo({
    title: t.title,
    description: t.description,
    keywords: complaints.flatMap((c) => c.keywords).join(", "),
    canonical: "/beschwerden",
    jsonLd,
    jsonLdId: "beschwerden-jsonld",
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="py-12 md:py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <nav aria-label={t.breadcrumbLabel} className="text-sm text-muted-foreground mb-6">
              <Link to={lp("/")} className="hover:text-primary">{t.home}</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">{t.complaints}</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4">
              {t.h1}
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              {t.intro}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid gap-6 sm:grid-cols-2">
              {complaints.map((c) => (
                <Card key={c.slug} className="border-border/60 hover:border-primary/40 transition-colors h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <h2 className="text-lg font-medium text-foreground mb-2">
                      <Link to={lp(`/beschwerden/${c.slug}`)} className="hover:text-primary">
                        {c.shortTitle}
                      </Link>
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">{c.metaDescription}</p>
                    <ul className="text-sm text-muted-foreground space-y-1 mb-5">
                      {c.synonyms.slice(0, 3).map((s) => (
                        <li key={s} className="flex gap-2">
                          <span className="text-primary mt-1.5 text-xs">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={lp(`/beschwerden/${c.slug}`)}
                      className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
                    >
                      {t.more} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Beschwerden;
